/**
 * Chart timeline presentation keyed on normalized record type (not broad timeline kind).
 * Inline expansion is only for session documentation — never inferred from soapNote/title.
 */

import type { MantineColor } from '@mantine/core';
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconArrowsExchange,
  IconCalendarEvent,
  IconChartHistogram,
  IconClipboardList,
  IconClockHour4,
  IconFileCertificate,
  IconFileCheck,
  IconFileDescription,
  IconFileUnknown,
  IconFlask,
  IconForms,
  IconGavel,
  IconHeartHandshake,
  IconIdBadge2,
  IconMail,
  IconMessage2,
  IconNotes,
  IconPhoneCall,
  IconPill,
  IconReceipt2,
  IconScale,
  IconSearch,
  IconShieldHeart,
  IconTargetArrow,
  IconUsersGroup,
  IconWritingSign,
  type Icon as TablerIcon,
} from '@tabler/icons-react';
import {
  isSessionDocumentationRecordType,
  normalizeMedicalRecordType,
} from '../data/medicalRecordTypes';
import type { ClinicalNote } from '../data/types';

/** Which existing page or modal opens a record. `none` means there is no viewer to open. */
export type ChartRecordViewMode =
  | 'appointment-page'
  | 'chart-document-page'
  | 'assessment-page'
  | 'discharge-summary-page'
  | 'admin-note-modal'
  | 'legacy-note-modal'
  | 'none';

export type ChartRecordCategory =
  | 'session'
  | 'measure'
  | 'treatment-planning'
  | 'form'
  | 'administrative'
  | 'discharge'
  | 'unknown';

export interface ChartRecordPresentation {
  /** Normalized record type, or {@link UNKNOWN_RECORD_TYPE} when it could not be resolved. */
  recordType: string;
  category: ChartRecordCategory;
  tone: MantineColor;
  icon: TablerIcon;
  canExpandInline: boolean;
  viewMode: ChartRecordViewMode;
}

/** Where the timeline row came from. Combined with `recordType` — never used on its own. */
export type ChartRecordSource =
  | 'appointment'
  | 'clinical-note'
  | 'admin-note'
  | 'discharge-summary'
  | 'assessment'
  | 'historical-record';

export interface ChartRecordContext {
  source: ChartRecordSource;
  /** Raw or normalized record type (`getRecordTypeForItem` output). */
  recordType?: string;
  /** Appointment rows: an appointment whose linked documentation is session documentation. */
  hasLinkedSessionNote?: boolean;
  /** QuestionnaireResponse id when the record is a chart document. */
  chartDocumentResponseId?: string;
}

export const UNKNOWN_RECORD_TYPE = 'Unknown';

interface RecordTypeStyle {
  category: ChartRecordCategory;
  tone: MantineColor;
  icon: TablerIcon;
}

const session = (icon: TablerIcon): RecordTypeStyle => ({ category: 'session', tone: 'blue', icon });
const measure = (icon: TablerIcon): RecordTypeStyle => ({ category: 'measure', tone: 'violet', icon });
const plan = (icon: TablerIcon): RecordTypeStyle => ({ category: 'treatment-planning', tone: 'green', icon });
/** Safety/risk documentation stays in the treatment-planning family but reads as urgent. */
const safety = (icon: TablerIcon): RecordTypeStyle => ({ category: 'treatment-planning', tone: 'orange', icon });
const form = (icon: TablerIcon): RecordTypeStyle => ({ category: 'form', tone: 'indigo', icon });
const operations = (icon: TablerIcon): RecordTypeStyle => ({ category: 'administrative', tone: 'gray', icon });
const dischargeStyle = (icon: TablerIcon): RecordTypeStyle => ({ category: 'discharge', tone: 'teal', icon });

const UNKNOWN_STYLE: RecordTypeStyle = { category: 'unknown', tone: 'gray', icon: IconFileUnknown };

/** Covers every value in `MEDICAL_RECORD_CATEGORY_SUBLISTS` plus the legacy singular aliases. */
const RECORD_TYPE_STYLES: Readonly<Record<string, RecordTypeStyle>> = {
  // Appointments — session documentation
  'Screening Note': session(IconSearch),
  'Consultation Note': session(IconMessage2),
  'Intake Note': session(IconClipboardList),
  'Progress Notes': session(IconNotes),
  'Case Review': session(IconFileDescription),
  'Crisis Session Note': session(IconAlertTriangle),
  'Psychotherapy Notes': session(IconWritingSign),
  'Family Therapy Progress Note': session(IconUsersGroup),
  'Couples Therapy Progress Note': session(IconHeartHandshake),
  'Group Therapy Progress Note': session(IconUsersGroup),
  'IOP Group Session Note': session(IconUsersGroup),

  // Measures
  Assessments: measure(IconChartHistogram),
  'Medication Orders': measure(IconPill),
  'Lab Orders': measure(IconFlask),

  // Treatment planning
  'Treatment Plan': plan(IconTargetArrow),
  'Safety Plan': safety(IconShieldHeart),
  'Risk Assessment': safety(IconAlertTriangle),
  'Discharge Summary': dischargeStyle(IconFileCheck),
  'Transfer Request': dischargeStyle(IconArrowsExchange),

  // Forms, consents and legal documents
  'Intake Forms': form(IconForms),
  ROIs: form(IconFileCertificate),
  'Consent Forms': form(IconWritingSign),
  'Legal Holds': form(IconGavel),
  'Court Documents': form(IconScale),
  'Custody Agreements': form(IconUsersGroup),
  'Good Faith Estimates': form(IconReceipt2),

  // Client operations
  'Inquiry Note': operations(IconMessage2),
  'Contact Attempts': operations(IconPhoneCall),
  'Collateral Contact Note': operations(IconPhoneCall),
  'Contact Logs': operations(IconPhoneCall),
  'Chart Notes': operations(IconNotes),
  'Incident Reports': operations(IconAlertOctagon),
  'Eligibility Notes': operations(IconIdBadge2),
  'Waitlist Entry': operations(IconClockHour4),
  'Waitlist Activity': operations(IconClockHour4),
  'Admin Notes': operations(IconClipboardList),
  'Administrative Note': operations(IconClipboardList),
  'Automated Emails': operations(IconMail),
};

export function resolveChartRecordPresentation(
  context: ChartRecordContext,
): ChartRecordPresentation {
  const recordType = normalizeMedicalRecordType(context.recordType);
  const style = recordType ? RECORD_TYPE_STYLES[recordType] : undefined;

  switch (context.source) {
    case 'appointment':
      // The row is the session itself: always the appointment page, always session styling.
      return {
        recordType: recordType ?? UNKNOWN_RECORD_TYPE,
        category: 'session',
        tone: 'blue',
        icon: IconCalendarEvent,
        canExpandInline: context.hasLinkedSessionNote === true,
        viewMode: 'appointment-page',
      };

    case 'assessment':
      return {
        recordType: recordType ?? 'Assessments',
        ...measure(IconChartHistogram),
        canExpandInline: false,
        viewMode: 'assessment-page',
      };

    case 'discharge-summary':
      return {
        recordType: recordType ?? 'Discharge Summary',
        ...dischargeStyle(IconFileCheck),
        canExpandInline: false,
        viewMode: 'discharge-summary-page',
      };

    case 'admin-note':
      return {
        recordType: recordType ?? 'Admin Notes',
        ...(style ?? operations(IconClipboardList)),
        canExpandInline: false,
        viewMode: 'admin-note-modal',
      };

    case 'clinical-note': {
      if (context.chartDocumentResponseId) {
        return {
          recordType: recordType ?? UNKNOWN_RECORD_TYPE,
          ...(style ?? UNKNOWN_STYLE),
          canExpandInline: false,
          viewMode: 'chart-document-page',
        };
      }
      if (!style) {
        // Partially migrated / unrecognized legacy row: no viewer we can vouch for.
        return {
          recordType: UNKNOWN_RECORD_TYPE,
          ...UNKNOWN_STYLE,
          canExpandInline: false,
          viewMode: 'none',
        };
      }
      return {
        recordType: recordType as string,
        ...style,
        canExpandInline: isSessionDocumentationRecordType(recordType),
        viewMode: 'legacy-note-modal',
      };
    }

    case 'historical-record':
      // Card click opens the PDF in a new tab — no inline "Show Note" expand.
      return {
        recordType: recordType ?? UNKNOWN_RECORD_TYPE,
        ...(style ?? UNKNOWN_STYLE),
        canExpandInline: false,
        viewMode: 'none',
      };
  }
}

export interface ChartRecordTargetIds {
  clientId: string;
  appointmentId?: string;
  chartDocumentResponseId?: string;
  assessmentTaskId?: string;
  /** Measure QuestionnaireResponse id — used for the Measures-tab fallback when no Task exists. */
  assessmentId?: string;
  dischargeSummaryId?: string;
}

export type ChartRecordTarget =
  | { kind: 'navigate'; to: string }
  | { kind: 'modal'; modal: 'admin-note' | 'legacy-note' }
  | { kind: 'none' };

/** Resolve the single destination for a record. Callers apply access checks before dispatching. */
export function resolveChartRecordTarget(
  viewMode: ChartRecordViewMode,
  ids: ChartRecordTargetIds,
): ChartRecordTarget {
  switch (viewMode) {
    case 'appointment-page':
      if (!ids.appointmentId) return { kind: 'none' };
      // Pass patientId so cohort Group sessions open that client's fan-out note
      // (shared note remains available via an in-page link).
      return {
        kind: 'navigate',
        to: ids.clientId
          ? `/dashboard/appointments/${ids.appointmentId}?patientId=${encodeURIComponent(ids.clientId)}`
          : `/dashboard/appointments/${ids.appointmentId}`,
      };

    case 'chart-document-page':
      return ids.chartDocumentResponseId
        ? {
          kind: 'navigate',
          to: `/dashboard/clients/${ids.clientId}/chart-document/${ids.chartDocumentResponseId}`,
        }
        : { kind: 'none' };

    case 'assessment-page':
      if (ids.assessmentTaskId) {
        return {
          kind: 'navigate',
          to: `/dashboard/clients/${ids.clientId}/measures/assessment/${ids.assessmentTaskId}`,
        };
      }
      return ids.assessmentId
        ? { kind: 'navigate', to: `/dashboard/clients/${ids.clientId}?tab=measures` }
        : { kind: 'none' };

    case 'discharge-summary-page':
      return ids.dischargeSummaryId
        ? {
          kind: 'navigate',
          to: `/dashboard/clients/${ids.clientId}/discharge-summary/${ids.dischargeSummaryId}`,
        }
        : { kind: 'none' };

    case 'admin-note-modal':
      return { kind: 'modal', modal: 'admin-note' };

    case 'legacy-note-modal':
      return { kind: 'modal', modal: 'legacy-note' };

    case 'none':
      return { kind: 'none' };
  }
}

/**
 * Whether a legacy note carries SOAP content that can actually be rendered inline.
 * Chart documents are excluded: their SOAP fields hold signature placeholders, not the document.
 */
export function hasRenderableSessionNoteContent(
  note: Pick<ClinicalNote, 'soapNote' | 'contentSections' | 'chartDocumentResponseId'> | undefined,
): boolean {
  if (!note || note.chartDocumentResponseId) return false;
  if (note.contentSections?.some((section) => section.text.trim().length > 0)) return true;
  const soap = note.soapNote;
  if (!soap) return false;
  return Object.values(soap).some((value) => typeof value === 'string' && value.trim().length > 0);
}
